/**
 * Ask CleanCleaver (docs/API.md "Ask CleanCleaver (2026-09-27)"): pure retrieval, the deterministic catalog engine,
 * shared by the API, the web demo and the phone app (FRAMEWORK T7: one rule set). Claude validation, redaction and
 * rate limiting stay in services/api/src/ask.ts.
 *
 * Reliability-first (FRAMEWORK T8): every answer is built from retrieved excerpts. Claude may only rephrase and
 * cite them; anything it returns that doesn't validate falls back to the catalog, and the catalog never invents.
 *
 * Gates (evidence class → control test in test/ask.test.ts):
 * | Q1 Grounding | Catalog answers only when ≥60% of the question's content words appear as whole words in one entry;
 *   else UNGROUNDED | MEASURED on LIBRARY: "fix my car", "what time is it", nonsense → grounded:false; wood board,
 *   test sanitizer, vomit → grounded. The 0.6 threshold is a JUDGMENT (strict on purpose, T8) |
 * | Q2 Claude cites only what it got | validateModelAnswer: exact shape, every id is a given excerpt, every item cites one
 *   and shares a word with it, grounded:false ⇔ exact UNGROUNDED | MEASURED: 7 fallback cases + pure validator controls.
 *   Word-overlap is a plausibility check, not proof of faithfulness (ASSUMED beyond that) |
 * | Q3 Fallback | Refusal, cut-off, non-JSON, API/network error, invalid output → catalog; no excerpts → Claude not
 *   called | MEASURED with a fake client |
 * | Q4 Privacy (T4) | Question redacted (employee names, phones, emails) before retrieval and before Claude; context
 *   reduced to screen + ids/kinds; audit stores length only | MEASURED: request/audit asserted free of name, phone,
 *   email, locationId. Names not on the staff list and free-text symptoms are NOT detected (ASSUMED acceptable:
 *   de-identified, and symptom words are needed for e.g. vomit cleanup) |
 */
import { noSanitizerFor, SEATING } from "./materials.ts";
import { easyFixes, getSurface, searchSurfaces, supplyKeyFor, SURFACES, type EasyFix, type SurfaceGuide } from "./surfaces.ts";
import { SUPPLY_SEEDS } from "./actions.ts";
import { getTemplate } from "./templates.ts";
import { howToFor } from "./howto.ts";
import { incidentInfo, INCIDENT_TYPES } from "./incidents.ts";
import type { IncidentType } from "./model.ts";
import { LIBRARY, searchLibrary, type LibraryTopic } from "./library.ts";
import { REQUIREMENTS } from "./requirements.ts";
import type { FindingKind } from "./schedule.ts";
import { thawAnswer } from "./thaw.ts";
import { haveAnswer } from "./haveit.ts";
import { isMessQuestion, messFromText, messPlan, messTitle } from "./mess.ts";
import { sopAnswer, type SopEnv } from "./sop.ts";
import { sanitizeAnswer } from "./sanitize.ts";
import { jobAnswer } from "./jobs.ts";
import type { Supply } from "./actions.ts";
import { closestHomeGuides, homeAnswer, isTiredQuestion, missingEquipment } from "./home/ask.ts";
import { pinnedAnswer } from "./askpinned.ts";
import type { HomeProfile } from "./home/profile.ts";
import type { DoneEntry } from "./home/score.ts";


/** Not covered, food-safety question (and the exact line Claude must return when its excerpts don't cover a question). */
export const UNGROUNDED = "Our guides don't cover that. Ask your county health department.";
/** Not covered, any other question: no county line, no "we won't guess about food safety". */
export const NOT_COVERED_YET = "We don't have a guide for that yet.";
export const QUESTION_MIN = 3;
export const QUESTION_MAX = 500;
/** A question is covered when at least this share of its content words appear in one excerpt. */
export const MIN_COVERAGE = 0.6;

export interface AskContext { screen?: string; /** The item the person is asking about ("We have this" flow, core haveit.ts): its sourced rule answers first. */ haveKey?: string; templateId?: string; surfaceId?: string; findingKind?: string; incidentType?: string; locationId?: string; /** The place's supply list, so job cards can say "you have it, kept at …" (never sent to a model). */ supplies?: Supply[]; /** Kitchen type for SOPs (sop.ts envFor(permitType)); the API sets it from the location, never from the client. */ env?: SopEnv; /** Asked from a home (HOME.md): home guides answer first. The API sets it from the org kind, never from the client. */ home?: boolean; homeProfile?: HomeProfile; /** The home's done-log, for "I'm tired, what's the least I can do?" (server-set, never from the client). */ homeLog?: DoneEntry[]; /** Clock for the done-log (tests, server). */ now?: Date }
/** The only context fields that ever leave the server (T4): screen and ids/kinds, no location, no person. */
export type ModelContext = Pick<AskContext, "screen" | "templateId" | "surfaceId" | "findingKind" | "incidentType">;

export type ExcerptKind = "library" | "surface" | "requirement" | "howto";
export interface Excerpt {
  kind: ExcerptKind;
  /** Unique within one ask, e.g. "library:cleaning-sanitizing", "surface:wood-cutting-board", "howto:sanitizer". */
  id: string;
  title: string;
  text: string;
  citations: string[];
  /** Whole-entry text used for coverage (text is the trimmed excerpt sent to the model). */
  searchText: string;
  /** Came from the caller's context (screen they're on), used as a tie-breaker. */
  fromContext: boolean;
  /** Short imperative steps this entry offers (catalog engine). */
  steps: string[];
}

export interface AskSource { kind: "library" | "surface" | "requirement"; id: string; title: string; citation?: string }
/** A tappable guide: a home guide (web /rooms/guide/:id, phone /home-guide?id=), the chores list, a surface or a library topic. */
/** "easy": an Easy procedure (core easy.ts EASY_PROCEDURES), opened in the step-by-step player; sent only as a closest guide the fallback picked. */
export interface AskLink { kind: "home_guide" | "home_chores" | "surface" | "library" | "easy"; id: string; title: string; /** "Step by step", "Closest guide"… */ note?: string }
/** An app action the fallback may suggest as a button (core content.ts APP_ACTIONS). Clients map the id to their own route. */
export interface AskAction { id: string; label: string }
/** What to do when the guides don't cover the question (no dead ends). */
export interface AskNext {
  /** The closest guides we do have, up to 3, by keyword. */
  guides: AskLink[];
  /** Offer "Add this to what we're writing next" (POST /orgs/:orgId/ask/requests). */
  request: boolean;
  /** Food-safety questions only: ask the county health department. */
  county: boolean;
  /** Who chose `guides`: our keyword match, or Claude choosing ONLY from our catalog ids (content.ts). Absent on older servers. */
  pickedBy?: "keywords" | "claude";
  /** One app action to offer as a button (Report, Nag, Order, Timers…), chosen from APP_ACTIONS only. */
  action?: AskAction;
}
export interface AskAnswer { answer: string; steps: string[]; fixes: EasyFix[]; sources: AskSource[]; grounded: boolean; engine: "claude" | "catalog"; /** Titled groups (SOPs: When, You need, Where the supplies live, Never…). Older clients use steps. */ sections?: { title: string; items: string[] }[]; /** Guides to open (step-by-step links, the chores list). */ links?: AskLink[]; /** Set when grounded is false: the next step instead of a dead end. */ next?: AskNext; /** Set by the server when grounded is false and the question was kept for the team (ask_unanswered). */ noted?: boolean; /** A left-out question (core leftout.ts): the slider opens on this item, time and place. */ leftOut?: import("./leftout.ts").LeftOutPreset; /** A cutting-board question (core boardclean.ts): the chips open on this material, food and setting. */ board?: import("./boardclean.ts").BoardPreset; /** A step-by-step answer drafted by Claude from handed sources (walkthrough.ts W6): "Drafted by CleanCleaver from …", its level and the plain level line. */ drafted?: { label: string; level: import("./walkthrough.ts").WalkLevel; line: string }; /** Server: a step-by-step answer is being written for this kept question (walkthrough.ts); poll GET /orgs/:orgId/ask/answers/:id. */ working?: { id: string; line: string; etaSeconds: number }; /** Before a card (core askcap.ts): the free questions left (only in the last 2), or `capped` when this one was not answered. */ askCap?: import("./askcap.ts").AskCapInfo }

// ---------- words ----------

const STOP = new Set(("a an and are as at be by can could do does did for from get got had has have how i if in into is it its " +
  "me my of on or our should so that the their them then there these this to too us was we what when where which who why will " +
  "with would you your about any some up out off time now today day thing things stuff help question go make use right good name phone email need needs want please tell know way ok okay just really much many ran run running runs used finished gone empty left happened guys hey buy order reorder purchase more someone somebody").split(" "));

/** Everyday words people use for the same thing ("threw up" → vomit), applied before matching. */
export function normalizeQuestion(q: string): string {
  return q
    .replace(/\b(threw|throw|throwing|thrown)\s+up\b|\bpuk(e|ed|ing)\b|\bbarf(ed|ing)?\b|\bvomit(ed|ing)?\b/gi, "vomit")
    .replace(/\bpoop(ed)?\b/gi, "diarrhea");
}

export function contentWords(q: string): string[] {
  const out: string[] = [];
  for (const w of q.toLowerCase().replace(/[’']/g, "").split(/[^a-z0-9°]+/)) if (w.length > 1 && !STOP.has(w) && !out.includes(w)) out.push(w);
  return out;
}

export const askNorm = (s: string) => s.toLowerCase().replace(/[’']/g, "").replace(/[^a-z0-9°]+/g, " ").trim();
function hasWordPrefix(text: string, w: string): boolean {
  let i = text.indexOf(w);
  while (i !== -1) {
    if (i === 0 || text[i - 1] === " ") return true;
    i = text.indexOf(w, i + 1);
  }
  return false;
}
const variants = (w: string) => (w.length > 3 && w.endsWith("s") && !w.endsWith("ss") ? [w, w.slice(0, -1)] : [w]);
/** Loose: some word starts with w ("clean" finds "cleanup"). Used only to pick a section, never to decide grounding. */
const hasWordLoose = (normText: string, w: string) => variants(w).some((v) => hasWordPrefix(normText, v));
const escRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
/** Strict: the word or a plain inflection ("test" finds "tests", "testing", "tested"; "car" never finds "card"). */
export function askHasWord(normText: string, w: string): boolean {
  return variants(w).some((v) => new RegExp(`(?:^| )${escRe(v)}(?:s|es|ed|ing|er|ers)?(?= |$)`).test(normText));
}

/** Share of the question's content words found in `text` as whole words (0 when the question has none). */
export function coverage(words: string[], text: string): number {
  if (!words.length) return 0;
  const t = askNorm(text);
  return words.filter((w) => askHasWord(t, w)).length / words.length;
}
const looseCoverage = (words: string[], text: string) => (words.length ? words.filter((w) => hasWordLoose(askNorm(text), w)).length / words.length : 0);

// ---------- retrieval ----------

const clip = (s: string, n = 1500) => (s.length > n ? `${s.slice(0, n - 1)}…` : s);
const bullets = (body: string) => body.split("\n").map((l) => l.replace(/^\s*(?:[-•*]|\d+[.)])\s*/, "").trim()).filter(Boolean);

/** Top library topics: all-words matches first, then topics matching the most single words (both via searchLibrary). */
function libraryTopics(words: string[], n: number): LibraryTopic[] {
  if (!words.length) return [];
  const out: LibraryTopic[] = [...searchLibrary(words.join(" "))];
  const tally = new Map<LibraryTopic, { hits: number; rank: number }>();
  for (const w of words) searchLibrary(w).forEach((t, i) => {
    const e = tally.get(t) ?? { hits: 0, rank: 0 };
    e.hits += 1; e.rank += 1 / (i + 1);
    tally.set(t, e);
  });
  const rest = [...tally.entries()].sort((a, b) => b[1].hits - a[1].hits || b[1].rank - a[1].rank || LIBRARY.indexOf(a[0]) - LIBRARY.indexOf(b[0])).map(([t]) => t);
  for (const t of rest) if (!out.includes(t)) out.push(t);
  return out.slice(0, n);
}

function libraryExcerpt(t: LibraryTopic, words: string[]): Excerpt {
  // Best section: judged on the question words the title doesn't already explain ("test" in "test sanitizer" picks
  // "Mixing and testing"); heading counts double; ties, or nothing left to judge, keep the first section.
  const title = askNorm(t.title);
  const rest = words.filter((w) => !hasWordLoose(title, w));
  let best = 0, bestScore = 0;
  if (rest.length) t.sections.forEach((s, i) => {
    const score = looseCoverage(rest, s.body) + 2 * looseCoverage(rest, s.heading);
    if (score > bestScore) { best = i; bestScore = score; }
  });
  const s = t.sections[best];
  const searchText = [t.title, t.summary, t.keywords.join(" "), ...t.sections.flatMap((x) => [x.heading, x.body])].join(" \n");
  return {
    kind: "library", id: `library:${t.id}`, title: t.title, fromContext: false,
    text: clip(s ? `${t.summary}\n${s.heading}:\n${s.body}` : t.summary),
    citations: s?.citations ?? [], searchText, steps: s ? bullets(s.body) : [],
  };
}

function surfaceExcerpt(s: SurfaceGuide, fromContext: boolean): Excerpt {
  const lines = [
    `Steps: ${s.steps.join(" | ")}`,
    s.howOften.length ? `How often: ${s.howOften.map((h) => h.when).join(" | ")}` : "",
    ...s.contingencies.map((c) => `If ${c.if}: ${c.then.join(" | ")}`),
    s.dont.length ? `Don't: ${s.dont.join(" | ")}` : "",
    ...s.products.map((p) => `Buy for ${p.purpose}: ${p.examples.join(", ")}${p.note ? ` (${p.note})` : ""}`),
  ].filter(Boolean);
  return {
    kind: "surface", id: `surface:${s.id}`, title: s.name, fromContext, text: clip(lines.join("\n"), 2000),
    citations: [...new Set([...s.citations, ...s.howOften.map((h) => h.citation), ...s.contingencies.flatMap((c) => c.citations)])],
    searchText: [s.name, ...s.keywords, ...(s.materials ?? []), ...lines].join(" \n"), steps: s.steps,
  };
}

const FINDING_REQUIREMENTS: Partial<Record<FindingKind, string[]>> = {
  // Same ids schedule.ts attaches to these findings; missed/failed checks come through templateId instead.
  not_checked_in: ["fc-employee-health", "fc-handwashing"],
  not_cleared: ["fc-employee-health", "fc-handwashing", "fc-bare-hand", "ut-food-handler-permit"],
  credential: ["ut-food-handler-permit"],
};

/** Requirement ids tied to the caller's context (template, finding kind, incident type). */
export function contextRequirementIds(ctx: AskContext): string[] {
  const ids: string[] = [];
  if (ctx.templateId) ids.push(...(getTemplate(ctx.templateId)?.requirementIds ?? []));
  if (ctx.findingKind) ids.push(...(FINDING_REQUIREMENTS[ctx.findingKind as FindingKind] ?? []));
  if (ctx.incidentType && INCIDENT_TYPES.some((t) => t.type === ctx.incidentType)) ids.push(...incidentInfo(ctx.incidentType as IncidentType).requirementIds);
  return [...new Set(ids)];
}

/** Everything the answer may draw on, in a stable order. Pure. */
/** The surface a mess question names, and its guide (surfaces.ts). */
const MESS_ON: [RegExp, string][] = [[/\bfloors?\b/, "kitchen-floor"], [/\b(counters?|prep table)\b/, "prep-counter"], [/\btables?\b/, "dining-tables"]];

export function retrieve(rawQuestion: string, ctx: AskContext = {}): Excerpt[] {
  const question = normalizeQuestion(rawQuestion);
  const words = contentWords(question);
  const out: Excerpt[] = [];

  if (ctx.templateId) {
    const h = howToFor(ctx.templateId);
    const tpl = getTemplate(ctx.templateId);
    if (h && tpl) out.push({
      kind: "howto", id: `howto:${tpl.id}`, title: `How to: ${tpl.title}`, fromContext: true,
      text: clip(`You need: ${h.need.join(" | ")}\nSteps: ${h.steps.join(" | ")}\nWhy: ${h.why}`),
      citations: h.citations, searchText: [tpl.title, ...h.need, ...h.steps, h.why].join(" \n"), steps: h.steps,
    });
  }

  // A home asking a home question: no restaurant surfaces, and only the library topics that hold at home (H4).
  const homeOnly = isHomeOnly(rawQuestion, ctx);
  const surfaces: SurfaceGuide[] = [];
  const ctxSurface = ctx.surfaceId ? getSurface(ctx.surfaceId) : undefined;
  if (ctxSurface) surfaces.push(ctxSurface);
  // A mess ("spilled milk on the floor", "egg on the floor") is about the surface it's on, never the food word in it:
  // the floor guide first, and no allergen guide or milk-pitcher guide (the floor→allergy regression, mess.ts).
  const mess = isMessQuestion(rawQuestion);
  const onWord = mess ? MESS_ON.find(([re]) => re.test(question))?.[1] : undefined;
  if (onWord && !homeOnly) { const g = getSurface(onWord); if (g && !surfaces.includes(g)) surfaces.push(g); }
  const fits = (s: SurfaceGuide) => !onWord || s.id === onWord || s.keywords.some((k) => MESS_ON.some(([re, id]) => id === onWord && re.test(k)));
  if (words.length && !homeOnly) for (const s of searchSurfaces(words.join(" "))) if (surfaces.length < 2 && !surfaces.includes(s) && fits(s)) surfaces.push(s);
  for (const s of surfaces) out.push(surfaceExcerpt(s, s === ctxSurface));

  for (const t of libraryTopics(words, 3)) if ((!homeOnly || HOME_LIBRARY.has(t.id)) && !(mess && t.category === "allergens")) out.push(libraryExcerpt(t, words));

  for (const id of contextRequirementIds(ctx)) {
    const r = REQUIREMENTS.find((x) => x.id === id);
    if (r) out.push({ kind: "requirement", id: `requirement:${r.id}`, title: r.title, fromContext: true, text: r.summary, citations: [r.citation], searchText: `${r.title} \n${r.summary}`, steps: [] });
  }
  return out;
}

// ---------- sources ----------

/** API sources have no "howto" kind: a how-to is reported as its template's first requirement, titled "How to: …". */
export function toSource(e: Excerpt, ctx: AskContext = {}): AskSource {
  const citation = e.citations.length ? e.citations.join("; ") : undefined;
  const raw = e.id.slice(e.id.indexOf(":") + 1);
  if (e.kind === "howto") {
    const reqId = (ctx.templateId && getTemplate(ctx.templateId)?.requirementIds[0]) || raw;
    return { kind: "requirement", id: reqId, title: e.title, ...(citation ? { citation } : {}) };
  }
  return { kind: e.kind, id: raw, title: e.title, ...(citation ? { citation } : {}) };
}

/** One source per kind+id, first wins (a how-to is reported under its template's requirement id). */
export function askDedupe(sources: AskSource[]): AskSource[] {
  const seen = new Set<string>();
  return sources.filter((s) => { const k = `${s.kind}:${s.id}`; if (seen.has(k)) return false; seen.add(k); return true; });
}

export function ungrounded(engine: AskAnswer["engine"]): AskAnswer {
  return { answer: UNGROUNDED, steps: [], fixes: [], sources: [], grounded: false, engine };
}

// ---------- home first, and no dead ends ----------

/** The question itself is about a business (restaurant, café, permit, inspector, prep table…): business guides may answer. */
const BUSINESS = /\b(restaurants?|cafe|café|coffee (shop|cart|business)|my (shop|business|cafe|café|truck)|the shop|business(es)?|mehk|microenterprise|commercial|customers?|employees?|staff|workers?|permits?|licensed|licen[cs]e|inspectors?|inspections?|health (code|inspection)|food code|food trucks?|carts?|catering|caterer|church hall|parish|hall kitchen|potlucks?|fundraisers?|prep (table|sink)|sanitizer bucket|(three|3)[- ]compartment|dish machine|walk-?in|service|shift)\b/;
export const isBusinessQuestion = (q: string) => BUSINESS.test(q.toLowerCase().replace(/[’']/g, ""));
/** In a home and not about a business: home guides only, never restaurant SOPs, sanitizer buckets, prep tables (H4). */
export const isHomeOnly = (q: string, ctx: AskContext) => !!ctx.home && !isBusinessQuestion(q);

/**
 * Library topics that hold at home (food science, not business rules). The rest are business rules (sanitizer
 * strengths, three-compartment sinks, dish machines, wiping-cloth buckets, restroom mops, sick workers, permits,
 * date marking, receiving, inspections, events) and never answer a home question. JUDGMENT, listed so it can be read.
 */
export const HOME_LIBRARY: ReadonlySet<string> = new Set(["cooking-temperatures", "reheating", "thawing", "cooling", "fridge-storage-order", "dry-storage-fifo", "allergens", "cross-contact", "handwashing", "cuts-burns-bandages", "pest-prevention", "pest-signs", "transporting-food"]);

/** Food-safety words: only these questions get the county health department line when not covered. */
const FOOD_SAFETY = /\b(food ?safety|safe to (eat|drink|use)|(is it|still) safe|unsafe|spoil(ed|s|ing)?|go(ne)? bad|expired?|past (its|the) date|use[- ]by|best[- ]by|sell[- ]by|mou?ld(y)?|raw|undercooked|under-?cooked|cross[- ]contam\w*|salmonella|listeria|e\.? ?coli|botulism|food ?borne|food poisoning|sick from|leftovers?|thaw\w*|defrost\w*|refreez\w*|reheat\w*|sit(ting|s)? out|left out|room temperature|internal temp\w*|temperatures?|°f|degrees|fridge|refrigerat\w*|freezer|frozen|freez\w*|allerg\w*|gluten|peanuts?|chicken|poultry|turkey|meat|beef|pork|fish|seafood|shellfish|eggs?|milk|dairy|cheese|rice|beans|canned|cans|dented|bulging|baby formula|formula|vomit|diarrhea|health department)\b/;
export const isFoodSafetyQuestion = (q: string) => FOOD_SAFETY.test(normalizeQuestion(q).toLowerCase().replace(/[’']/g, ""));

/**
 * Words that name the job, not the thing: they say nothing about which guide is close. "clean" matched "Cleaning vs
 * sanitizing" (its title word "Cleaning" counts as "clean" through the -ing ending), so a question about a leather couch
 * was handed the sanitizer guide (2026-10-03). The object and the material have to carry the match.
 */
const JOB_WORDS = /^(clean(s|ed|er|ers|ing)?|wash(es|ed|ing)?|fix(es|ed|ing)?|remove[sd]?|removing|rid|get(ting)?|best|easy|easiest|quick(ly)?|safe(ly)?|properly|correct(ly)?|steps?|guide|instructions?|tips?)$/;

/** A repair or build question ("fix a wobbly table"): our guides are about cleaning, so none is close unless the question also cleans. */
const REPAIR = /\b(fix(ing)?|repair(ing)?|wobbl\w*|broken|tighten|assemble|paint(ing)?)\b/;
const CLEANS = /\b(clean\w*|sanitiz\w*|sanitis\w*|wash\w*|wipe|wiping|disinfect\w*|stain\w*|spill\w*)\b/;

/** Library topics and surfaces about food-contact sanitizing: sanitizer strengths, sinks, dish machines, cloth buckets. */
const SANITIZING_TOPIC = /sanitiz|sanitis|chlorine|quat\b|three-compartment|dishwasher|dish machine|wiping cloth/;

/**
 * Never offer a food-contact sanitizing guide for a non-food material (sanitizer and bleach can damage leather, cloth and
 * finishes) unless the question is about food-contact use. `kind`/`id` as in an AskLink.
 */
export function sanitizingGuideForbidden(question: string, kind: string, id: string): boolean {
  if (!noSanitizerFor(question)) return false;
  if (kind === "library") { const t = LIBRARY.find((x) => x.id === id); return !!t && SANITIZING_TOPIC.test(`${t.id} ${t.title} ${t.keywords.join(" ")}`.toLowerCase()); }
  if (kind === "surface") return getSurface(id)?.kind === "food_contact";
  return false;
}

/** Up to 3 closest guides we do have, by keyword: home guides in a home (for its own equipment), else surfaces and library. */
export function closestGuides(question: string, ctx: AskContext = {}, n = 3): AskLink[] {
  if (isHomeOnly(question, ctx)) return closestHomeGuides(question, ctx.homeProfile, n).map((g) => ({ kind: "home_guide", id: g.id, title: g.title, note: "Closest guide" }));
  if (REPAIR.test(question.toLowerCase()) && !CLEANS.test(question.toLowerCase())) return [];
  // The job words ("clean", "wash", "fix") don't pick a guide; the object and its material do. Nothing left: no guess.
  const words = contentWords(normalizeQuestion(question)).filter((w) => !JOB_WORDS.test(w));
  if (!words.length) return [];
  const score = (title: string, keys: string[]) => words.reduce((s, w) => s + (askHasWord(askNorm(title), w) ? 3 : 0) + (askHasWord(askNorm(keys.join(" ")), w) ? 2 : 0), 0);
  const cands: { link: AskLink; s: number }[] = [
    ...SURFACES.map((x) => ({ link: { kind: "surface" as const, id: x.id, title: x.name, note: "Closest guide" }, s: score(x.name, [...x.keywords, ...(x.materials ?? [])]) })),
    ...LIBRARY.map((t) => ({ link: { kind: "library" as const, id: t.id, title: t.title, note: "Closest guide" }, s: score(t.title, [...t.keywords, t.summary]) })),
  ].filter((c) => c.s >= 3 && !sanitizingGuideForbidden(question, c.link.kind, c.link.id)); // a title word, or two keyword words: "car" alone never points at "Transporting food"
  return cands.sort((a, b) => b.s - a.s).slice(0, n).map((c) => c.link);
}

/**
 * Not covered, with a next step that fits the question (no dead ends): the closest guides, "add this to what we're
 * writing next", and the county line for food-safety questions only.
 */
export function notCovered(question: string, ctx: AskContext = {}, engine: AskAnswer["engine"] = "catalog"): AskAnswer {
  const food = isFoodSafetyQuestion(question);
  const missing = ctx.home ? missingEquipment(question, ctx.homeProfile) : undefined;
  const answer = missing ? `We don't have a guide for ${missing.yours ? "your" : "a"} ${missing.label} yet.${food ? " Ask your county health department about food safety." : ""}` : food ? UNGROUNDED : NOT_COVERED_YET;
  return { answer, steps: [], fixes: [], sources: [], grounded: false, engine, next: { guides: closestGuides(question, ctx), request: true, county: food } };
}

// ---------- catalog engine ----------

const PRIORITY: Record<ExcerptKind, number> = { surface: 3, howto: 2, library: 1, requirement: 0 };

/** The excerpt that best covers the question, or undefined when none reaches MIN_COVERAGE. Deterministic. */
export function bestExcerpt(question: string, excerpts: Excerpt[]): Excerpt | undefined {
  const words = contentWords(question);
  if (!words.length) return undefined;
  let best: { e: Excerpt; cov: number; tie: number } | undefined;
  for (const e of excerpts) {
    if (e.kind === "requirement" || !e.steps.length) continue;
    const cov = coverage(words, e.searchText);
    // Ties go to the entry whose title names the thing ("Vomit and diarrhea cleanup" over a counter guide).
    const tie = (e.fromContext ? 10 : 0) + coverage(words, e.title) * 5 + PRIORITY[e.kind];
    if (!best || cov > best.cov || (cov === best.cov && tie > best.tie)) best = { e, cov, tie };
  }
  return best && best.cov >= MIN_COVERAGE ? best.e : undefined;
}

/**
 * A question that describes a situation ("we ran out of sanitizer") is answered from the contingency written for
 * exactly that situation, when every content word of the question appears in the contingency's "if" line.
 */
function situationAnswer(question: string, ctx: AskContext): AskAnswer | undefined {
  // "How do I…?" asks for a guide; "We ran out of…" describes what happened. Only the second is a situation,
  // and a check's own how-to (context.templateId) always wins.
  if (ctx.templateId || /^\s*(how|what|when|where|why|which|can|could|should|do|does|is|are)\b/i.test(question)) return undefined;
  const words = contentWords(question);
  if (!words.length) return undefined;
  // "ran out / out of / no more / empty" is the situation itself: then only "Out of …" contingencies qualify.
  const outOf = /\b(ran out|run out|running out|out of|no more|none left|empty)\b/i.test(question);
  for (const sf of SURFACES) for (const c of sf.contingencies) {
    if (outOf !== /\bout of\b/i.test(c.if)) continue;
    const ifWords = contentWords(c.if);
    if (words.every((w) => ifWords.some((x) => x === w || x.startsWith(w) || w.startsWith(x)))) {
      const fixes: EasyFix[] = [...c.then.map((text) => ({ kind: "do" as const, text, citations: c.citations, surfaceId: sf.id })), ...easyFixes(sf.id).filter((f) => f.kind === "buy")];
      return { answer: `${c.if}: here's what to do.`, steps: c.then, fixes, sources: [{ kind: "surface", id: sf.id, title: sf.name, citation: c.citations[0] }], grounded: true, engine: "catalog" };
    }
  }
  return undefined;
}

/** Products people ask to buy or ran out of: the supplies catalog plus every product the surface guides recommend. */
function productAnswer(question: string, ctx: AskContext): AskAnswer | undefined {
  // Only when someone wants to get more of something; "how do I test sanitizer" is a how-to, not a shopping list.
  if (ctx.templateId || !/\b(buy|order|reorder|purchase|ran out|run out|running out|out of|need more|restock|where (do|can) (i|we) (get|buy))\b/i.test(question)) return undefined;
  const words = contentWords(question);
  if (!words.length || words.length > 4) return undefined;
  type P = { name: string; supplyKey?: string; examples: string[]; uses: { surface: SurfaceGuide; purpose: string }[] };
  const products = new Map<string, P>();
  for (const sp of SUPPLY_SEEDS) products.set(`supply:${sp.key}`, { name: sp.name, supplyKey: sp.key, examples: sp.note ? [sp.note] : [], uses: [] });
  for (const sf of SURFACES) for (const pick of sf.products) {
    const key = supplyKeyFor(pick);
    for (const ex of pick.examples) {
      const id = key ? `supply:${key}` : `ex:${ex}`;
      const p = products.get(id) ?? { name: key ? SUPPLY_SEEDS.find((x) => x.key === key)?.name ?? pick.purpose : ex, supplyKey: key, examples: [], uses: [] };
      if (!p.examples.includes(ex)) p.examples.push(ex);
      if (!p.uses.some((u) => u.surface.id === sf.id)) p.uses.push({ surface: sf, purpose: pick.purpose });
      products.set(id, p);
    }
  }
  let best: { p: P; hit: string } | undefined;
  for (const p of products.values()) for (const label of [p.name, ...p.examples]) {
    if (words.every((w) => askHasWord(askNorm(label), w) || askHasWord(askNorm(label), w.replace(/s$/, "")))) { if (!best || label.length < best.hit.length) best = { p, hit: label }; }
  }
  if (!best) return undefined;
  const { p, hit } = best;
  const uses = p.uses.slice(0, 4);
  return {
    answer: `${hit}: how to get more, and what it's for.`,
    steps: [
      p.supplyKey ? "Reorder it with the Buy button below, or in Settings → Supplies (where it's kept and who to call)." : "Buy it from your usual supplier.",
      ...uses.map((u) => `Used for: ${u.purpose} (${u.surface.name}).`),
    ],
    fixes: [{ kind: "buy", text: `${p.name}: ${p.examples.slice(0, 3).join(" or ") || hit}`, supplyKey: p.supplyKey, examples: p.examples, citations: uses.flatMap((u) => u.surface.citations).slice(0, 3) }],
    sources: uses.map((u) => ({ kind: "surface" as const, id: u.surface.id, title: u.surface.name, citation: u.surface.citations[0] })),
    grounded: true, engine: "catalog",
  };
}

export function catalogAnswer(rawQuestion: string, excerpts: Excerpt[], ctx: AskContext = {}): AskAnswer {
  const question = normalizeQuestion(rawQuestion);
  // Thaw / refreeze / keep-time questions have exact, sourced answers (thaw.ts): numbers never come from retrieval.
  const thaw = thawAnswer(question);
  if (thaw) return thaw;
  // A food, supply or equipment item the place says it has (haveit.ts): its own sourced rule and questions.
  const have = haveAnswer(question, ctx.haveKey);
  if (have) return have;
  // The questions Ask suggests, and the same questions said another way (askpinned.ts): the answer to THE question, before
  // any guide that only shares a word with it. Business questions only (a home's own guides answer a home, H4).
  const pinned = pinnedAnswer(question, ctx);
  if (pinned) return pinned;
  // In a home, the home guides (chores, SOPs, placement, tired mode) answer; business SOPs only when the question is
  // about a business (H4). Library topics that hold at home come next; otherwise a next step, never a restaurant SOP.
  if (isHomeOnly(question, ctx)) {
    const home = homeAnswer(question, ctx.homeProfile, { log: ctx.homeLog, now: ctx.now });
    if (home) return home;
    // Food science that holds at home answers food-safety questions only; equipment we have no guide for says so (H2).
    const e = isFoodSafetyQuestion(question) && !missingEquipment(question) ? bestExcerpt(question, excerpts.filter((x) => x.kind === "library" && HOME_LIBRARY.has(x.id.slice("library:".length)))) : undefined;
    return e ? excerptAnswer(e, [], ctx, question) : notCovered(question, ctx);
  }
  // "How do I clean X" / "where do I keep X": the full SOP for this kitchen type (sop.ts).
  // "How do I sanitize the counter after chicken / the grill?" → that exact situation (sanitize.ts), then full SOPs.
  // "Clean the espresso machine", "raw chicken on the wood board": the job card (what, with what, have it?, where, put back).
  const job = ctx.templateId ? undefined : jobAnswer(question, ctx.supplies);
  if (job) return job;
  const san = ctx.templateId ? undefined : sanitizeAnswer(question);
  if (san) return san;
  // A home asking about its home business (MEHK): the home-kitchen SOPs.
  const sop = ctx.templateId ? undefined : sopAnswer(question, ctx.env ?? (ctx.home ? "home_kitchen" : undefined)); // a check's own how-to wins on its screen
  if (sop) return sop;
  const situation = situationAnswer(question, ctx);
  if (situation) return situation;
  const product = productAnswer(question, ctx);
  if (product) return product;
  const mess = messAnswer(rawQuestion, ctx);
  if (mess) return mess;
  const e = bestExcerpt(question, excerpts);
  if (!e) {
    // Outside a home, household questions ("how often should I wash my sheets?") are answered only when the kitchen
    // guides have nothing: a restaurant's own guides always win over home guidance.
    // Tired mode reads a home's own chores, so a business never gets it.
    const household = ctx.home || ctx.templateId || isTiredQuestion(question) ? undefined : homeAnswer(question);
    return household ?? notCovered(question, ctx);
  }
  return excerptAnswer(e, excerpts.filter((x) => x.kind === "requirement"), ctx, question);
}

/**
 * "Spilled milk and crumbs on the floor": the mess steps for that mix, in order (mess.ts messPlan), never the allergen
 * guide a food word would pull in. Each step keeps its citation.
 */
function messAnswer(rawQuestion: string, ctx: AskContext): AskAnswer | undefined {
  // A mess on a booth, couch or other seating is the seating guide's, never the floor's (also from its own surface screen:
  // "How do I clean the spills, vomit and body fluids on seating?", measured 2026-10-07).
  if (SEATING.test(rawQuestion.toLowerCase()) || /\bseating\b/i.test(rawQuestion) || ctx.surfaceId === "seating-spills") return undefined;
  const m = messFromText(rawQuestion);
  if (!m) return undefined;
  const plan = messPlan(m.kinds, m.surface, undefined, { scope: ctx.home ? "home" : "business" });
  const surfaceId = m.surface === "floor" ? "kitchen-floor" : m.surface === "table" ? "dining-tables" : "prep-counter";
  const g = getSurface(surfaceId);
  return {
    answer: `${messTitle(m.kinds, m.surface, "")}: here's what to do, in this order.`,
    steps: plan.steps.map((s) => s.say),
    fixes: plan.steps.map((s) => ({ kind: "do" as const, text: s.say, citations: s.citation ? [s.citation] : s.source ? [s.source] : [] })),
    sections: [{ title: "Why this order", items: plan.why }],
    sources: g ? [{ kind: "surface", id: g.id, title: g.name, citation: g.citations[0] }] : [],
    grounded: true, engine: "catalog",
  };
}

function excerptAnswer(e: Excerpt, supporting: Excerpt[], ctx: AskContext, question: string): AskAnswer {
  const sources = askDedupe([e, ...supporting].map((x) => toSource(x, ctx)));
  if (e.kind === "surface") {
    const s = getSurface(e.id.slice("surface:".length))!;
    const fixes = easyFixes(s.id, question);
    return { answer: `${s.name}: here's what to do.`, steps: fixes.filter((f) => f.kind === "do").map((f) => f.text), fixes, sources, grounded: true, engine: "catalog" };
  }
  const steps = e.steps.slice(0, 8);
  const lead = e.kind === "howto" ? `${e.title}.` : `${e.title}. ${LIBRARY.find((t) => `library:${t.id}` === e.id)?.summary ?? ""}`.trim();
  return { answer: lead, steps, fixes: steps.map((text) => ({ kind: "do", text, citations: e.citations })), sources, grounded: true, engine: "catalog" };
}


/** The whole catalog engine in one call: validate length, retrieve, answer. Used by the API fallback, web demo and phone. */
export function askCatalog(question: string, ctx: AskContext = {}): AskAnswer {
  const q = question.trim();
  if (q.length < QUESTION_MIN) {
    // Opened on a check with nothing typed: the check's own how-to is the answer.
    const how = ctx.templateId ? howToFor(ctx.templateId) : undefined;
    const tpl = ctx.templateId ? getTemplate(ctx.templateId) : undefined;
    if (!how || !tpl) return notCovered(q, ctx);
    return { answer: `How to: ${tpl.title}. ${how.why}`, steps: how.steps, fixes: how.steps.map((text) => ({ kind: "do", text, citations: how.citations })),
      sources: [{ kind: "requirement", id: tpl.requirementIds[0] ?? tpl.id, title: `How to: ${tpl.title}`, citation: how.citations[0] }], grounded: true, engine: "catalog" };
  }
  return catalogAnswer(q.slice(0, QUESTION_MAX), retrieve(q, ctx), ctx);
}
